// Per-user view/display settings for FlowchartBuilder — comment/badge visibility,
// Gantt timeline crop/zoom/filters, etc. These used to live as shared columns on
// `flowcharts` (every collaborator saw the same toggle) or, for 4 of them, as
// localStorage-only state (per-browser, not per-account). Both are now one row per
// (user_id, flowchart_id) in `user_flowchart_view_settings`, following the exact
// DB-primary/localStorage-fallback/one-time-migrate-up pattern already established
// for `user_mindmap_priorities` in MindmapBuilder.tsx.
//
// This hook owns no React state of its own — all 21 values keep living as individual
// useStates in FlowchartBuilder.tsx, same as before. It's pull-based (fetchViewSettings
// is called/awaited explicitly from load(), not from an internal effect) so it resolves
// before any of those states are set, instead of racing them.
import { useCallback } from 'react';
import supabase from './supabase';
import { logger } from './lib/logger';
import {
  lsGetJSON,
  lsGanttResourceFilter, lsGanttCategoryFilter,
  lsFilteredExportEnabled, lsExportSelectedBoxIds,
} from './lib/localStorage';
import type { VisibleLevels, DurationUnit } from './types/index';

// mode === 'offset' ignores day/date/year/time entirely (offset granularity comes
// from the separate, shared durationUnit instead); they're only meaningful for
// 'date'. See useGanttSchedule.ts's getStart/getEnd for the string assembly that
// consumes this (month is dropped whenever day+date+time are all true).
export interface EndTimeDisplay {
  mode: 'date' | 'offset';
  day: boolean;
  date: boolean;
  year: boolean;
  time: boolean;
}

export const DEFAULT_END_TIME_DISPLAY: EndTimeDisplay = { mode: 'date', day: false, date: true, year: false, time: false };

export interface UserFlowchartViewSettings {
  canvasShowComments: boolean | null;
  canvasShowSubitemBadges: boolean | null;
  canvasShowAreas: boolean | null;
  canvasShowUidBadges: boolean | null;
  canvasViewAllFields: boolean | null;
  endTimeDisplay: EndTimeDisplay | null;
  ganttVisibleLevels: VisibleLevels | null;
  ganttLabelWidth: number | null;
  ganttZoom: number | null;
  ganttTimelineView: { start: string; end: string } | null;
  ganttHideNonWorkingHours: boolean | null;
  ganttHideNonWorkingDays: boolean | null;
  ganttOnlyVisibleBars: boolean | null;
  ganttDurationDisplay: DurationUnit | null;
  ganttShowSubitems: boolean | null;
  ganttShowAreas: boolean | null;
  ganttShowRegularBoxes: boolean | null;
  ganttShowStartEndColumn: boolean | null;
  ganttResourceFilter: string[] | null;
  ganttCategoryFilter: string[] | null;
  ganttFilteredExportEnabled: boolean | null;
  ganttExportSelectedBoxIds: number[] | null;
}

interface UseUserFlowchartViewSettingsParams {
  userId: string | undefined;
}

export function useUserFlowchartViewSettings({ userId }: UseUserFlowchartViewSettingsParams) {
  const fetchViewSettings = useCallback(async (flowchartId: string): Promise<UserFlowchartViewSettings | null> => {
    if (!userId) return null;
    const { data: row } = await supabase
      .from('user_flowchart_view_settings')
      .select('*')
      .eq('user_id', userId)
      .eq('flowchart_id', flowchartId)
      .maybeSingle();

    // One-time client->DB migration for the 4 formerly-localStorage-only settings —
    // same pattern as user_mindmap_priorities' localStorage->DB migration in
    // MindmapBuilder.tsx. Only runs when the DB value is still null; once migrated,
    // the DB row wins on every subsequent load. The other 17 fields don't need this —
    // they were already seeded for existing users by the one-time backfill migration.
    const migrateUp: Record<string, unknown> = {};

    let ganttResourceFilter: string[] | null = row?.gantt_resource_filter ?? null;
    if (ganttResourceFilter === null) {
      const stored = lsGetJSON<string[]>(lsGanttResourceFilter(flowchartId), []);
      if (stored.length > 0) { ganttResourceFilter = stored; migrateUp.gantt_resource_filter = stored; }
    }
    let ganttCategoryFilter: string[] | null = row?.gantt_category_filter ?? null;
    if (ganttCategoryFilter === null) {
      const stored = lsGetJSON<string[]>(lsGanttCategoryFilter(flowchartId), []);
      if (stored.length > 0) { ganttCategoryFilter = stored; migrateUp.gantt_category_filter = stored; }
    }
    let ganttFilteredExportEnabled: boolean | null = row?.gantt_filtered_export_enabled ?? null;
    if (ganttFilteredExportEnabled === null) {
      const stored = lsGetJSON<boolean | null>(lsFilteredExportEnabled(flowchartId), null);
      if (stored !== null) { ganttFilteredExportEnabled = stored; migrateUp.gantt_filtered_export_enabled = stored; }
    }
    let ganttExportSelectedBoxIds: number[] | null = row?.gantt_export_selected_box_ids ?? null;
    if (ganttExportSelectedBoxIds === null) {
      const stored = lsGetJSON<number[]>(lsExportSelectedBoxIds(flowchartId), []);
      if (stored.length > 0) { ganttExportSelectedBoxIds = stored; migrateUp.gantt_export_selected_box_ids = stored; }
    }

    if (Object.keys(migrateUp).length > 0) {
      // Fire-and-forget: don't block the UI on this write.
      supabase.from('user_flowchart_view_settings').upsert(
        { user_id: userId, flowchart_id: flowchartId, ...migrateUp, updated_at: new Date().toISOString() },
        { onConflict: 'user_id,flowchart_id' }
      ).then(() => {}, (err: unknown) => logger.error('[viewSettings] localStorage->DB migrate-up failed:', err));
    }

    return {
      canvasShowComments: row?.canvas_show_comments ?? null,
      canvasShowSubitemBadges: row?.canvas_show_subitem_badges ?? null,
      canvasShowAreas: row?.canvas_show_areas ?? null,
      canvasShowUidBadges: row?.canvas_show_uid_badges ?? null,
      canvasViewAllFields: row?.canvas_view_all_fields ?? null,
      endTimeDisplay: row?.end_time_display ?? null,
      ganttVisibleLevels: row?.gantt_visible_levels ?? null,
      ganttLabelWidth: row?.gantt_label_width ?? null,
      ganttZoom: row?.gantt_zoom ?? null,
      ganttTimelineView: row?.gantt_timeline_view ?? null,
      ganttHideNonWorkingHours: row?.gantt_hide_non_working_hours ?? null,
      ganttHideNonWorkingDays: row?.gantt_hide_non_working_days ?? null,
      ganttOnlyVisibleBars: row?.gantt_only_visible_bars ?? null,
      ganttDurationDisplay: row?.gantt_duration_display ?? null,
      ganttShowSubitems: row?.gantt_show_subitems ?? null,
      ganttShowAreas: row?.gantt_show_areas ?? null,
      ganttShowRegularBoxes: row?.gantt_show_regular_boxes ?? null,
      ganttShowStartEndColumn: row?.gantt_show_start_end_column ?? null,
      ganttResourceFilter, ganttCategoryFilter, ganttFilteredExportEnabled, ganttExportSelectedBoxIds,
    };
  }, [userId]);

  // Centralized upsert — replaces all 17 individual updateFlowchartMeta(currentId, {col})
  // call sites AND the 4 write-only lsSet(...)-only call sites.
  const updateViewSetting = useCallback((flowchartId: string, fields: Record<string, unknown>) => {
    if (!userId) return Promise.resolve();
    return supabase.from('user_flowchart_view_settings').upsert(
      { user_id: userId, flowchart_id: flowchartId, ...fields, updated_at: new Date().toISOString() },
      { onConflict: 'user_id,flowchart_id' }
    ).then(() => {}, (err: unknown) => logger.error('[viewSettings] update failed:', err));
  }, [userId]);

  return { fetchViewSettings, updateViewSetting };
}
